import { onAuthStateChanged, signOut } from 'firebase/auth'
import { doc, getDoc } from 'firebase/firestore'
import { getFunctions, httpsCallable } from 'firebase/functions'
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { auth, db, getUserDeviceInfo, getUserIP } from './firebase.jsx'

export const PermissionsContext = createContext({
  user: null,
  permissions: {},
  sessionId: null,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  handleSignOut: () => {},
})

const defaultPermissions = Object.freeze({
  'View Doorknocking Client': true,
  'Edit Profile': true,
  'Edit User Settings': true,
})

const adminPermissions = Object.freeze({
  'View Mapping Client': true,
  'Create Subaccounts': true,
  'Remove Subaccounts': true,
  'View Premium Data': true,
  'Build and Export Datasets': true,
  'Can View Eigel Tools': true,
  'View Election Simulator': true,
  'View Voter Contacts': true,
  'View Fundraising Analytics': true,
  'Volunteer Management': true,
})

const superAdminPermissions = Object.freeze({
  'Manage Users': true,
  'Manage Database': true,
  'View Sessions': true,
  'Security Audit': true,
  'System Configuration': true,
})

// Memoize merged permissions to avoid object recreation
const allAdminPermissions = Object.freeze({ ...defaultPermissions, ...adminPermissions })
const allSuperAdminPermissions = Object.freeze({ ...allAdminPermissions, ...superAdminPermissions })

// Super admin emails - users with full system access (fallback list)
// Primary method is checking isSuperAdmin field in Firestore user document
const SUPER_ADMIN_EMAILS = [
  'alonsdale@bernoullitechnologies.net',
  'tonylonsdale@bernoullitechnologies.net',
  'alons3253@gmail.com',
]

export const getActiveSessions = async (userId) => {
  const sessionDocRef = doc(db, 'sessions', userId)

  try {
    const docSnap = await getDoc(sessionDocRef)

    if (docSnap.exists() && docSnap.data().isValid) {
      const sessionData = docSnap.data()
      return [{
        sessionId: docSnap.id,
        ...sessionData,
        icon: sessionData.deviceType === 'mobile' ? 'mobile' : 'desktop',
        lastActive: sessionData.lastActive?.toDate().toLocaleString() || 'Unknown',
      }]
    } else {
      console.warn('No active session or session is not valid')
      return []
    }
  } catch (error) {
    console.error('Error getting active sessions:', error)
    return []
  }
}


export const PermissionsProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [permissions, setPermissions] = useState({})
  const [sessionId, setSessionId] = useState(() => localStorage.getItem('sessionId') || '')
  const intervalSetRef = useRef(false)
  const functionsRef = useRef(null)

  // Lazily initialize Firebase functions to avoid recreation
  const getFunctionsRef = useCallback(() => {
    if (!functionsRef.current) {
      functionsRef.current = getFunctions()
    }
    return functionsRef.current
  }, [])

  const handleSignOut = useCallback(async () => {
    try {
      if (sessionId) {
        const functions = getFunctionsRef()
        const invalidateSession = httpsCallable(functions, 'invalidateSession')
        await invalidateSession({ sessionId })
      }
    } catch (error) {
      console.error('Error during sign out:', error)
    } finally {
      await signOut(auth)
      localStorage.removeItem('sessionId')
      localStorage.removeItem('sessionStartTime')
      setSessionId('')
      setUser(null)
      setPermissions({})
    }
  }, [sessionId, getFunctionsRef])

  // Session validation effect
  useEffect(() => {
    if (!sessionId || intervalSetRef.current) return

    const functions = getFunctionsRef()
    const validateSession = httpsCallable(functions, 'validateSession')

    const validate = async () => {
      try {
        if (auth.currentUser && sessionId) {
          const result = await validateSession({ sessionId })
          if (!result.data.isValid) {
            console.error('Session is invalid')
            handleSignOut()
          }
        }
      } catch (error) {
        console.error('Error validating session:', error)
      }
    }

    validate()
    const intervalId = setInterval(validate, 600000) // 10 minutes
    intervalSetRef.current = true

    return () => {
      clearInterval(intervalId)
      intervalSetRef.current = false
    }
  }, [sessionId, handleSignOut, getFunctionsRef])

  // Auth state change effect
  useEffect(() => {
    const functions = getFunctionsRef()
    const createSession = httpsCallable(functions, 'createSession')

    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        try {
          const userDocRef = doc(db, 'users', currentUser.uid)
          const docSnap = await getDoc(userDocRef)

          if (docSnap.exists()) {
            const userData = docSnap.data()
            const userIsAdmin = !!userData.isAdministrator
            // Check both Firestore isSuperAdmin field AND email list for flexibility
            const userIsSuperAdmin = !!userData.isSuperAdmin || SUPER_ADMIN_EMAILS.includes(currentUser.email?.toLowerCase())

            setUser(currentUser)
            setPermissions(
              userIsSuperAdmin ? allSuperAdminPermissions :
              userIsAdmin ? allAdminPermissions :
              defaultPermissions,
            )

            // Only create session if we don't have one
            const existingSessionId = localStorage.getItem('sessionId')
            if (!existingSessionId) {
              const deviceInfo = getUserDeviceInfo()
              const ipAddress = await getUserIP()

              const sessionData = {
                device: deviceInfo.deviceModel,
                ip: ipAddress,
                os: `${deviceInfo.osName} ${deviceInfo.osVersion}`,
                screenResolution: deviceInfo.screenResolution,
                userAgent: deviceInfo.fullUserAgent,
                browser: `${deviceInfo.browserName} ${deviceInfo.browserVersion}`,
              }

              const sessionResult = await createSession(sessionData)
              const newSessionId = sessionResult.data.sessionId
              localStorage.setItem('sessionId', newSessionId)
              setSessionId(newSessionId)
            }
          } else {
            handleSignOut()
          }
        } catch (error) {
          console.error('Error processing auth state change:', error)
        }
      } else {
        setUser(null)
        setPermissions({})
      }
    })

    return () => unsubscribe()
  }, [getFunctionsRef, handleSignOut])

  // Memoize context value to prevent unnecessary re-renders of consumers
  const contextValue = useMemo(() => ({
    user,
    permissions,
    handleSignOut,
    sessionId,
  }), [user, permissions, handleSignOut, sessionId])

  return (
    <PermissionsContext.Provider value={contextValue}>
      {children}
    </PermissionsContext.Provider>
  )
}

export const usePermissions = () => useContext(PermissionsContext)
export { SUPER_ADMIN_EMAILS }
